import { useOmniboxEnrichment } from "@/contexts/OmniboxEnrichmentContext.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { useEffect } from "react";
import {
  buildTimeRangeCalendarContext,
  buildTimeRangeMessage,
  type MatchedEvent,
  type TimeRange,
} from "./time-range-selection.ts";
import { TimeRangeChips } from "@/components/messages/Omnibox/TimeRangeChips.tsx";

const ENRICHMENT_ID = "calendar-time-range";

/**
 * Registers a time-range enrichment into the OmniboxEnrichmentContext.
 * When ranges are selected, renders TimeRangeChips and provides a buildMessage
 * that includes the time range context in the submitted message.
 *
 * `matchedEvents` is no longer used in the wire payload (the server fetches
 * events from `selection.ranges` itself, post-BIG-4941). It stays on the
 * selection object because chip rendering still needs it locally.
 */
export function useTimeRangeEnrichment(selection: {
  ranges: TimeRange[];
  matchedEvents: MatchedEvent[];
  removeRange: (rangeId: string) => void;
  clearAll: () => void;
}) {
  const { register, unregister } = useOmniboxEnrichment();
  const { getUserTimezone } = useCurrentUserContext();

  useEffect(() => {
    if (selection.ranges.length === 0) {
      unregister(ENRICHMENT_ID);
      return;
    }

    register({
      id: ENRICHMENT_ID,
      renderContent: () => (
        <TimeRangeChips ranges={selection.ranges} onRemove={selection.removeRange} />
      ),
      buildMessage: (text: string) => {
        if (selection.ranges.length === 0) return undefined;
        const tz = getUserTimezone();
        const { content } = buildTimeRangeMessage(text, selection.ranges, tz);
        const calendarContext = buildTimeRangeCalendarContext(text, selection.ranges, tz);
        return { text: content, calendarContext };
      },
      onEscape: () => {
        selection.clearAll();
        return true;
      },
      // Declares which scheduling-flow fields this enrichment answers out-of-
      // band so the guided flow can skip the matching user-facing steps. Here:
      // the selected ranges imply "when?". Duration is NOT auto-skipped — we
      // always confirm meeting length with the user, even for a single range.
      provides: { when: true },
    });
  }, [
    selection.ranges,
    selection.removeRange,
    selection.clearAll,
    register,
    unregister,
    getUserTimezone,
  ]);

  // Unregister on unmount
  useEffect(() => {
    return () => unregister(ENRICHMENT_ID);
  }, [unregister]);
}
